import { createSignal } from "solid-js";
import type { PowerSyncDrizzleDB, Todo } from "@listto/powersync";
import {
  createTodoMutation,
  type createTodoMutation as CreateTodoMutationType,
  deleteTodoMutation,
  toggleTodoMutation,
} from "@listto/shared";

// Index of the optional extra props argument in createTodoMutation
const MUTATION_OPTIONS_INDEX = 3;

export const EXIT_DURATION_MS = 200;
const NEW_ITEM_ANIMATION_DURATION_MS = 500;

interface UseTodoMutationsOptions {
  userId: () => string | undefined;
  listId: () => string;
  defaultAddTodoProps?: () =>
    | Parameters<typeof CreateTodoMutationType>[typeof MUTATION_OPTIONS_INDEX]
    | undefined;
  drizzle: () => PowerSyncDrizzleDB | null | undefined;
  todoInput: () => HTMLInputElement | null;
  currentListTodos: () => Todo[] | undefined;
}

export const useTodoMutations = (options: UseTodoMutationsOptions) => {
  const [newTodoText, setNewTodoText] = createSignal("");
  const [isSubmitting, setIsSubmitting] = createSignal(false);
  const [newlyAddedIds, setNewlyAddedIds] = createSignal<Set<string>>(new Set());
  const [exitingIds, setExitingIds] = createSignal<Set<string>>(new Set());
  const [todoToDelete, setTodoToDelete] = createSignal<Todo | null>(null);

  const handleAddTodo = async (e: Event) => {
    e.preventDefault();
    const text = newTodoText().trim();
    const dbInstance = options.drizzle();
    const uid = options.userId();
    if (!text || !uid || !dbInstance || isSubmitting()) {
      return;
    }
    setIsSubmitting(true);
    try {
      const result = await createTodoMutation(
        dbInstance,
        uid,
        text,
        options.defaultAddTodoProps?.(),
      );
      if (result) {
        setNewlyAddedIds((prev) => new Set([...prev, result]));
        setTimeout(() => {
          setNewlyAddedIds((prev) => {
            const next = new Set(prev);
            next.delete(result);
            return next;
          });
        }, NEW_ITEM_ANIMATION_DURATION_MS);
      }
      setNewTodoText("");
    } catch (err: unknown) {
      console.error("Error creating todo:", err instanceof Error ? err.message : String(err));
    } finally {
      setIsSubmitting(false);
      setTimeout(() => {
        options.todoInput()?.focus();
      }, 0);
    }
  };

  const handleToggleTodo = async (todo: Todo) => {
    const dbInstance = options.drizzle();
    if (dbInstance) {
      await toggleTodoMutation(dbInstance, todo.id, todo.completed === 1, todo).catch(
        (err: unknown) => {
          console.error("Error toggling todo:", err instanceof Error ? err.message : String(err));
        },
      );
    }
  };

  const handleDeleteTodo = (id: string) => {
    const todo = options.currentListTodos()?.find((item) => item.id === id);
    if (todo) {
      setTodoToDelete(todo);
    }
  };

  const executeDeleteTodo = (id: string) => {
    const dbInstance = options.drizzle();
    if (!dbInstance || exitingIds().has(id)) {
      return;
    }

    setExitingIds((prev) => new Set([...prev, id]));

    setTimeout(() => {
      void (async () => {
        try {
          await deleteTodoMutation(dbInstance, id);
        } catch (err: unknown) {
          console.error("Error deleting todo:", err instanceof Error ? err.message : String(err));
        } finally {
          setExitingIds((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
        }
      })();
    }, EXIT_DURATION_MS);
  };

  return {
    newTodoText,
    setNewTodoText,
    isSubmitting,
    newlyAddedIds,
    exitingIds,
    setExitingIds,
    todoToDelete,
    setTodoToDelete,
    handleAddTodo,
    handleToggleTodo,
    handleDeleteTodo,
    executeDeleteTodo,
  };
};
